/**
 * Whether the value on screen came from the AI:
 * - `none`      — no successful AI generation this edit session.
 * - `untouched` — AI produced the current value and the user hasn't edited it.
 * - `edited`    — AI produced a value and the user then modified it.
 *
 * The editor tracks this to decide whether to show the "Generated by AI"
 * badge. Hosts that need it for their own purposes track their own copy — the
 * component overview keeps it in its edit-session store to report an
 * accepted/edited/discarded outcome — so the transitions live here and both
 * sides apply the same ones.
 */
export type AiDescriptionProvenance = 'none' | 'untouched' | 'edited';

const onGenerated = (): AiDescriptionProvenance => 'untouched';

const onUserEdit = (
  provenance: AiDescriptionProvenance
): AiDescriptionProvenance =>
  provenance === 'untouched' ? 'edited' : provenance;

export const aiDescriptionProvenanceOperations = {
  initial: 'none' as AiDescriptionProvenance,
  onGenerated,
  onUserEdit,
};

export type ComponentToDescribe = {
  /** Empty when the component has no name yet; generation is then unavailable. */
  name: string;
  typeName: string;
  workspaceName: string;
};

/**
 * Analytics for the generation flow. Supplied by the host app because tracking
 * is app-level, and each surface reports a different location.
 */
export type ComponentDescriptionTracking = {
  onGenerationAttempted: VoidFunction;
  onGenerationSucceeded: VoidFunction;
  onGenerationDeclinedByLlm: (reason: string) => void;
};

export type AiDescriptionGeneration =
  | { isEnabled: false }
  | { isEnabled: true; tracking: ComponentDescriptionTracking };
